import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ComplianceBanner from "@/components/ComplianceBanner";
import { Calculator, Syringe, TestTube, ArrowRight } from "@phosphor-icons/react";

const DOSE_UNITS = [
  { k: "mcg", label: "mcg", toMg: (v) => v / 1000 },
  { k: "mg",  label: "mg",  toMg: (v) => v },
  { k: "iu",  label: "IU (Semaglutide/Tirz.)", toMg: (v) => v * 0.001 }, // treated as mcg-equivalent shorthand; leave as-is for research use
];

function fmt(n, digits = 2) {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

export default function DosageCalculator() {
  const [products, setProducts] = useState([]);
  const [presetSlug, setPresetSlug] = useState("");
  const [vialMg, setVialMg] = useState(10);
  const [bacMl, setBacMl] = useState(2);
  const [doseVal, setDoseVal] = useState(250);
  const [doseUnit, setDoseUnit] = useState("mcg");
  const [syringeType, setSyringeType] = useState("100"); // U-100 default

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const applyPreset = (slug) => {
    setPresetSlug(slug);
    if (!slug) return;
    const p = products.find((x) => x.slug === slug);
    if (!p) return;
    // Attempt to parse "10mg" / "1500mg" from the variant label or subtitle
    const label = (p.variants?.[0]?.label || p.subtitle || "").toLowerCase();
    const m = label.match(/(\d+(?:\.\d+)?)\s*mg/);
    if (m) setVialMg(Number(m[1]));
  };

  const calc = useMemo(() => {
    const vial = Number(vialMg) || 0;
    const bac  = Number(bacMl)  || 0;
    const dv   = Number(doseVal) || 0;
    const unit = DOSE_UNITS.find((u) => u.k === doseUnit) || DOSE_UNITS[0];
    const doseMg = unit.toMg(dv);
    const concMgPerMl = bac > 0 ? vial / bac : 0;                 // mg per mL
    const drawMl     = concMgPerMl > 0 ? doseMg / concMgPerMl : 0; // mL per dose
    const units      = drawMl * Number(syringeType);               // insulin units on selected syringe
    const doses      = doseMg > 0 ? vial / doseMg : 0;             // doses per vial
    return { doseMg, concMgPerMl, drawMl, units, doses };
  }, [vialMg, bacMl, doseVal, doseUnit, syringeType]);

  const warn = calc.units > 100
    ? "Draw exceeds a full 100-unit syringe — increase BAC water or split the dose."
    : calc.drawMl > 0 && calc.units < 2
    ? "Very small volume — consider adding more BAC water for accuracy."
    : null;

  return (
    <div className="page-enter" data-testid="dosage-calculator">
      <ComplianceBanner />

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 pb-8">
          <div className="overline mb-3 flex items-center gap-2">
            <Calculator size={16} weight="duotone" /> Research tool
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
            Reconstitution &<br /><span className="accent-slash">dose calculator.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            Enter your vial size, the BAC water you're adding, and your target dose.
            We'll do the mL and insulin-unit math — for laboratory research use only.
          </p>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
          {/* Inputs */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 rounded-2xl p-6 space-y-5">
              <div>
                <div className="overline mb-2 flex items-center gap-2"><TestTube size={14} weight="duotone" /> Vial</div>
                <label className="block text-sm text-zinc-400 mb-1">Load a PRMU product (optional)</label>
                <select
                  value={presetSlug}
                  onChange={(e) => applyPreset(e.target.value)}
                  className="w-full border border-white/15 rounded-lg px-3 py-2 bg-white/[0.03] text-sm outline-none focus:border-blue-500"
                  data-testid="calc-preset"
                >
                  <option value="">Manual entry</option>
                  {products.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name} — {p.variants?.[0]?.label || ""}</option>
                  ))}
                </select>
              </div>

              <NumberField
                label="Peptide in vial"
                unit="mg"
                value={vialMg}
                onChange={setVialMg}
                testid="calc-vial-mg"
                step="0.5"
              />
              <NumberField
                label="BAC water added"
                unit="mL"
                value={bacMl}
                onChange={setBacMl}
                testid="calc-bac-ml"
                step="0.1"
              />
            </div>

            <div className="border border-white/10 rounded-2xl p-6 space-y-5">
              <div className="overline mb-2 flex items-center gap-2"><Syringe size={14} weight="duotone" /> Dose</div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Target dose per injection</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={doseVal}
                    onChange={(e) => setDoseVal(e.target.value)}
                    className="flex-1 border border-white/15 rounded-lg px-3 py-2 bg-white/[0.03] font-mono outline-none focus:border-blue-500"
                    data-testid="calc-dose-value"
                  />
                  <select
                    value={doseUnit}
                    onChange={(e) => setDoseUnit(e.target.value)}
                    className="border border-white/15 rounded-lg px-3 py-2 bg-white/[0.03] text-sm outline-none focus:border-blue-500"
                    data-testid="calc-dose-unit"
                  >
                    {DOSE_UNITS.map((u) => <option key={u.k} value={u.k}>{u.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Syringe</label>
                <div className="flex gap-2">
                  {[["100","U-100 (1 mL = 100 units)"],["50","U-50 (0.5 mL = 50 units)"],["40","U-40 (older insulin syr.)"]].map(([v,l])=> (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setSyringeType(v)}
                      className={`flex-1 border rounded-lg px-3 py-2 text-xs ${syringeType===v ? "border-blue-500 bg-blue-500/10 text-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}
                      data-testid={`calc-syringe-${v}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-7">
            <div className="border border-white/10 rounded-2xl p-8 h-full bg-gradient-to-br from-white/[0.03] to-transparent">
              <div className="overline mb-6 flex items-center gap-2">Result <ArrowRight size={14} weight="bold" /></div>

              <div className="grid sm:grid-cols-2 gap-4">
                <ResultCard label="Draw per dose" value={`${fmt(calc.drawMl, 3)} mL`} accent testid="calc-result-ml" />
                <ResultCard label="On U-100 syringe" value={`${fmt(calc.units, 1)} units`} accent testid="calc-result-units" />
                <ResultCard label="Concentration" value={`${fmt(calc.concMgPerMl, 3)} mg/mL`} testid="calc-result-conc" />
                <ResultCard label="Doses per vial" value={fmt(calc.doses, 1)} testid="calc-result-doses" />
              </div>

              {warn && (
                <div className="mt-6 border border-amber-400/40 bg-amber-400/[0.06] rounded-xl p-4 text-sm text-amber-200" data-testid="calc-warn">
                  ⚠ {warn}
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-white/10 text-xs font-mono uppercase tracking-widest text-zinc-500 space-y-2">
                <div>Formula: concentration = vial / BAC water  ·  draw = dose ÷ concentration  ·  units = draw × syringe scale</div>
                <div>For laboratory research use only. Not for human or veterinary use.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function NumberField({ label, unit, value, onChange, testid, step }) {
  return (
    <div>
      <label className="block text-sm text-zinc-400 mb-1">{label}</label>
      <div className="flex items-center border border-white/15 rounded-lg bg-white/[0.03] focus-within:border-blue-500 overflow-hidden">
        <input
          type="number"
          value={value}
          step={step || "1"}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 bg-transparent font-mono outline-none"
          data-testid={testid}
        />
        <span className="px-3 py-2 text-xs font-mono text-zinc-500 border-l border-white/10">{unit}</span>
      </div>
    </div>
  );
}

function ResultCard({ label, value, accent, testid }) {
  return (
    <div className={`rounded-xl p-5 border ${accent ? "border-blue-500/40 bg-blue-500/[0.06]" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className={`font-display text-3xl font-bold ${accent ? "text-blue-400" : "text-white"}`}>{value}</div>
    </div>
  );
}
